import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Loading from "../components/Loading";
import {
  getPermittedRoutes,
  RouteConfig,
  routesConfig,
} from "../config/routes";
import { httpService } from "../services/httpService";
import { useAuth } from "../hooks";
import { IPermissionCategories } from "../features/Configurations/TeamManagement/interface";
import { PERMISSION_MAPPER } from "../shared/constants";
import toast from "react-hot-toast";

// 🔍 Improved matcher (prefix + dynamic support) — pure function, no component state
const matchRoute = (pathname: string, routePath: string): boolean => {
  const pathSegments = pathname.split("/").filter(Boolean);
  const routeSegments = routePath.split("/").filter(Boolean);

  if (routeSegments.length > pathSegments.length) return false;

  return routeSegments.every((segment, index) => {
    if (segment.startsWith(":")) return true;
    return segment === pathSegments[index];
  });
};

// Correct resolution: submenu FIRST, most-specific match wins — pure function, no component state
const resolveRoute = (pathname: string) => {
  let currentRoute: RouteConfig | undefined;
  let submenuRoute: RouteConfig | undefined;

  for (const route of routesConfig) {
    if (route.submenu) {
      const foundSub = route.submenu.find((sub) =>
        matchRoute(pathname, sub.path)
      );
      if (foundSub) {
        return { currentRoute: route, submenuRoute: foundSub };
      }
    }
    if (matchRoute(pathname, route.path)) {
      // Prefer the most specific match (most path segments) so that "/" (0 segments)
      // never silently overwrites a concrete public route like "/auth" (1 segment).
      const routeSpecificity = route.path.split("/").filter(Boolean).length;
      const currentSpecificity = currentRoute
        ? currentRoute.path.split("/").filter(Boolean).length
        : -1;
      if (routeSpecificity > currentSpecificity) {
        currentRoute = route;
      }
    }
  }

  return { currentRoute, submenuRoute };
};

const RouteInspector: React.FC = () => {
  const { user, isVip, isLoading, logout, subscriptionDetails } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasCheckedRoute, setHasCheckedRoute] = useState(false);

  useEffect(() => {
    if (isLoading) return;

    const accessToken = httpService.getAccessToken();
    const { currentRoute, submenuRoute } = resolveRoute(location.pathname);

    // ✅ Public route
    if (!currentRoute?.protected) {

      setHasCheckedRoute(true);
      return;
    }

    // 🔐 No token
    if (!accessToken) {

      if (location.pathname !== "/auth") {
        navigate("/auth");
      }
      setHasCheckedRoute(true);
      return;
    }

    // ❌ Invalid user
    if (!user) {

      httpService.clearAllData();
      navigate("/auth");
      setHasCheckedRoute(true);
      return;
    }

    // 👥 Team member gate — runs first so pricing redirect applies even to full-access users
    // (custom_admin users land here too; their user_permissions is normalized to SCHEMA_LEVEL_ACCESS)
    const isTeamMember = !user.is_team_admin && !!user.parent;

    if (isTeamMember) {
      const parent = user.parent!;
      const parentHasActiveSubscription = parent?.plan !== undefined && parent?.plan !== null

      // Parent has neither VIP nor an active plan → org has no access, force logout
      if (!parent.is_vip && !parentHasActiveSubscription) {
        toast.error(
          "Your organization doesn't have an active plan. Please contact your team admin to restore access."
        );
        logout();
        setHasCheckedRoute(true);
        return;
      }

      // Team members never reach the pricing page — redirect to dashboard
      if (location.pathname === '/pricing') {
        const permittedRoutes = getPermittedRoutes(user);
        navigate(permittedRoutes[0]?.path || '/dashboard');
        setHasCheckedRoute(true);
        return;
      }

      // Parent has an active plan/VIP — fall through to full-access bypass or permission checks
    }

    // 👑 Full access bypass — superuser, "*", VIP schema-level access, or custom_admin (normalized above)
    if (
      user.is_superuser ||
      user.user_permissions === "*" ||
      user.user_permissions === PERMISSION_MAPPER.SCHEMA_LEVEL_ACCESS
    ) {
      setHasCheckedRoute(true);
      return;
    }

    // 💳 Subscription gate — runs BEFORE whitelist so fresh accounts can't slip through via
    // whitelisted paths like /plans-and-billing or /billing.
    if (!isTeamMember) {
      const hasActiveSubscription = user?.plan !== undefined && user?.plan !== null;

      // Cancelled users who previously subscribed retain read-only portal access
      const isCancelledFormerSubscriber = user.subscription_status === 'canceled' && !!user.plan;

      // Users who have ever used a trial retain access even if plan is now null
      const hasTrialHistory = subscriptionDetails?.has_used_trial === true;

      if (!isVip && !hasActiveSubscription && !isCancelledFormerSubscriber && !hasTrialHistory) {
        const allowedWithoutPlan = ['/pricing', '/profile', '/payment/verify'];
        if (!allowedWithoutPlan.includes(location.pathname)) {
          navigate('/pricing');
        }
        setHasCheckedRoute(true);
        return;
      }
    }

    // ✅ Whitelisted routes (only reached by users who passed the subscription gate above)
    if (
      [
        "/profile",
        "/user-activity",
        "/support",
        "/pricing",
        "/billing",
        "/billing-history",
        "/plans-and-billing",
      ].includes(location.pathname)
    ) {
      setHasCheckedRoute(true);
      return;
    }

    // 🚨 Route not found
    if (!currentRoute && !submenuRoute) {
      navigate("/404");
      setHasCheckedRoute(true);
      return;
    }

    const permittedRoutes = getPermittedRoutes(user);

    // 🔐 Permission resolution (submenu priority)
    const permission = submenuRoute?.permission ?? currentRoute?.permission;

    const isUserRoleAllowed = permission
      ? (user.user_permissions as IPermissionCategories).modules?.some(
        (p) => p.codename === permission
      )
      : true;

    // 🚫 Block access
    if (!isUserRoleAllowed) {
      const firstPath = permittedRoutes?.[0]?.path || "/404";

      // Safety check: only navigate if we are moving to a DIFFERENT path
      if (location.pathname !== firstPath) {
        setHasCheckedRoute(true);
        navigate(firstPath);
        return;
      }
    }

    setHasCheckedRoute(true);
  }, [user, isVip, isLoading, location.pathname, navigate, logout, subscriptionDetails?.has_used_trial]);

  if (isLoading || !hasCheckedRoute) {
    return <Loading />;
  }

  return <Outlet />;
};

export default RouteInspector;